import DatePicker from "@components/Booking/SlotPicking/components/DatePicker";
import useDatePickerControls from "@components/Booking/SlotPicking/hooks/useDatePickerControls";
import {ApptSlot} from "@services/monolith/availability";
import {memoize} from "lodash";
import {useTranslation} from "@i18n/client";
import React, {SetStateAction, memo, useCallback, useEffect, useMemo, useState} from "react";
import {SpecialtyId} from "src/constants/specialtyIds";
import {useFetchSlotsInArea} from "src/queries/useFetchSlotsInArea";
import {v4} from "uuid";

import {guess} from "../../../../_services/api";
import {I18nNamespace} from "@i18n";
import {getStartOfDay} from "../../../../utils/timeUtils";
import SlotGroup from "./components/SlotGroup";
import Sooner from "./components/Sooner";
import {emitChangeDateEvent, emitSlotOptionsViewedEvent} from "./events";
import {useAppendDoctorsToSlotGroups} from "./hooks/useAppendDoctorsToSlotGroups";
import PrimaryCareSlots from "./PrimaryCareSlots";
import {formatMinOpen} from "./utils/formatMinOpen";
import {MonolithLocationFields} from "@services/graphql/queries/location";

export type Props = {
  initialCalendarTime: number | null;
  onSlotSelection: (slot: ApptSlot, practiceId: string, locationSlug: string) => unknown;
  reasonId?: string;
  specialtyId: string;
  selectedLocationFirstLine?: string;
  location: MonolithLocationFields;
  slotOptionsId: string;
  setSlotOptionsId: React.Dispatch<SetStateAction<string>>;
  viewId: string;
  loading?: boolean;
  disabled?: boolean;
  patientId?: string;
};

const Slots = ({
  selectedLocationFirstLine,
  location,
  onSlotSelection,
  reasonId,
  specialtyId,
  initialCalendarTime,
  slotOptionsId,
  setSlotOptionsId,
  viewId,
  loading = false,
  disabled = false,
  patientId,
}: Props) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);

  const selectedLocationTimezone = location?.timezone;

  const [selectedSlotTime, setSelectedSlotTime] = useState(0);
  const timezone = selectedLocationTimezone ?? guess();

  /**
   * Returns a function used to generate location based slot selector functions.
   * The returned function is memoized to optimize rendering in loops.
   */
  const getSlotSelector = useMemo(() => {
    return memoize((location: {practiceId: string; slug: string}) => {
      return (slot: ApptSlot) => {
        onSlotSelection(slot, location.practiceId, location.slug);
        setSelectedSlotTime(slot.time);
      };
    });
  }, [onSlotSelection, setSelectedSlotTime]);

  const apptReasonWithHardcodedSpecialtyIds__NEED_TO_FIX_THIS = useMemo(
    () => ({
      id: reasonId || "",
      specialtyIds: [specialtyId],
    }),
    [reasonId, specialtyId],
  );

  const {
    currentCalendarTime,
    onPlusDay: _onPlusDay,
    onMinusDay: _onMinusDay,
  } = useDatePickerControls(timezone, initialCalendarTime);

  const slotParams = useMemo(
    () => ({
      from: getStartOfDay(currentCalendarTime, timezone),
      limit: 144,
      days: 0,
      timezone,
      patientId,
    }),
    [currentCalendarTime, timezone, patientId],
  );

  const slotsQuery = useFetchSlotsInArea(
    location,
    apptReasonWithHardcodedSpecialtyIds__NEED_TO_FIX_THIS,
    slotParams,
    specialtyId === SpecialtyId.PRIMARY_CARE,
  );

  const onPlusDay = useCallback(() => {
    emitChangeDateEvent("forward", slotOptionsId, viewId);
    _onPlusDay();
  }, [_onPlusDay, slotOptionsId, viewId]);

  const onMinusDay = useCallback(() => {
    emitChangeDateEvent("backward", slotOptionsId, viewId);
    _onMinusDay();
  }, [_onMinusDay, slotOptionsId, viewId]);

  const msgNoSlotsAvailable = t("No Slots Available!", {ns: "web-scheduling"});

  useEffect(() => {
    if (slotsQuery.isSettled) {
      const options = slotsQuery.data.initialLocation.slots;
      const nearbyOptions = slotsQuery.data.nearbyLocations.map(l => l.slots).flat();
      const allSlots = [...options, ...nearbyOptions];
      const numberOfSlotsInADay = allSlots.length;
      const openMinutes = formatMinOpen(timezone, location.hours, currentCalendarTime);
      const soonestSlotTime = allSlots.sort((a, b) => a.time - b.time)[0]?.time;
      const emptyListText = allSlots.length === 0 ? msgNoSlotsAvailable : null;

      const newSlotOptionsId = v4();
      setSlotOptionsId(newSlotOptionsId);

      emitSlotOptionsViewedEvent({
        slotOptionsId: newSlotOptionsId,
        openMinutes,
        soonestSlotTime,
        numberOfSlotsInADay,
        emptyListText,
        options,
        nearbyOptions,
        viewId,
        limitedAvailabilityFlag: slotsQuery.data.availabilityIsLimited,
      });
    }
  }, [
    currentCalendarTime,
    location.hours,
    location.id,
    msgNoSlotsAvailable,
    setSlotOptionsId,
    slotsQuery.data.availabilityIsLimited,
    slotsQuery.data.initialLocation.slots,
    slotsQuery.data.nearbyLocations,
    slotsQuery.isSettled,
    timezone,
    viewId,
  ]);

  const {initialLocation, nearbyLocations} = slotsQuery.data;

  const slotsGroupedByProvider = useAppendDoctorsToSlotGroups({
    slotsQuery,
    skip: specialtyId !== SpecialtyId.PRIMARY_CARE,
  });

  return (
    <div className="flex flex-col items-center gap-5">
      <DatePicker
        currentCalendarTime={currentCalendarTime}
        onMinusDay={onMinusDay}
        onPlusDay={onPlusDay}
        selectedLocationFirstLine={selectedLocationFirstLine}
        timezone={timezone}
      />
      {specialtyId === SpecialtyId.PRIMARY_CARE ? (
        <PrimaryCareSlots
          slotsGroupedByProvider={slotsGroupedByProvider.data}
          selectedSlotTime={selectedSlotTime}
          getOnSlotSelection={getSlotSelector}
          loading={loading || slotsQuery.isLoading || slotsGroupedByProvider.loading}
          disabled={disabled}
        />
      ) : (
        <>
          <SlotGroup
            key={initialLocation.location.id}
            timezone={initialLocation.location.timezone}
            slots={initialLocation.slots}
            selectedSlotTime={selectedSlotTime}
            onSlotSelection={getSlotSelector(initialLocation.location)}
            loading={loading || !slotsQuery.isSettled}
            disabled={disabled}
          />
          <Sooner slotsQuery={slotsQuery} />
          {nearbyLocations.map(l => {
            return (
              <SlotGroup
                key={l.location.id}
                title={l.location.name}
                dist={l.location.dist}
                timezone={l.location.timezone}
                slots={l.slots}
                selectedSlotTime={selectedSlotTime}
                onSlotSelection={getSlotSelector(l.location)}
                loading={loading || !slotsQuery.isSettled}
                disabled={disabled}
                withBorder
              />
            );
          })}
        </>
      )}
    </div>
  );
};

export default memo(Slots);
